// src/components/admin/DatePickerField.tsx
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { EditButton } from './EditButton';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerFieldProps {
  /** EditableFormField と同様、メインラベルを表示する場合 */
  label: React.ReactNode;
  /** EditableFormField の subLabel と同じ位置に表示したい文言 (例: (日本語表記)) */
  subLabel?: string;
  value: string;
  onChange: (value: string) => void;
  /** 表示領域の高さ */
  height?: number;
}

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  subLabel, // ← 追加
  value,
  onChange,
  height = 33,
}) => {
  // "yyyy.M.d" 形式の文字列を Date オブジェクトに変換
  const parseDate = (str: string): Date | null => {
    const parts = str.split('.');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      const y = parseInt(year, 10);
      const m = parseInt(month, 10) - 1;
      const d = parseInt(day, 10);
      return new Date(y, m, d);
    }
    return null;
  };

  // Date オブジェクトを "yyyy.M.d" 形式の文字列に変換
  const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}.${m}.${d}`;
  };

  const initialDate = value ? parseDate(value) : null;
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);

  /** モーダル（カレンダー）表示フラグ */
  const [open, setOpen] = useState(false);

  const handleChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      onChange(formatDate(date));
    } else {
      onChange('');
    }
  };

  return (
    <div className="mb-6 border-b border-gray-400 w-full">
      {/* 大ラベル： EditableFormField と同じ w-1/6 */}
      <div className="flex items-start my-3 w-full">
        <label className="font-bold w-1/6">{label}</label>

        {/* 右側： w-5/6 */}
        <div className="w-5/6">
          {/* 1行だけの場合でも、構造を合わせる */}
          <div className="flex items-center mb-2 w-full">
            {/* サブラベル: EditableFormField の subLabel と同じ位置 w-1/6 */}
            <span className="w-1/6 text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
              {subLabel}
            </span>
            {/* 実際の日付表示欄: w-4/6 で高さ固定 */}
            <div
              onClick={() => setOpen(true)}
              className="border border-gray-400 px-2 bg-white cursor-pointer w-4/6
                         overflow-hidden whitespace-nowrap text-ellipsis"
              style={{ height: `${height}px` }}
            >
              {selectedDate ? formatDate(selectedDate) : ''}
            </div>
            {/* 編集ボタン: w-1/6 */}
            <div className="ml-2 w-1/6">
              <EditButton onClick={() => setOpen(true)} />
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">{label}の編集</h2>
            <div className="flex items-start mb-4 w-full">
              {/* カレンダーをインライン表示 */}
              <DatePicker
                selected={selectedDate}
                onChange={(date) => handleChange(date)}
                inline
              />
            </div>
            <div className="mt-6 flex flex-wrap justify-end">
              <button
                onClick={() => setOpen(false)}
                className="mr-4 mb-2 px-4 py-2 bg-gray-200 rounded"
              >
                編集をキャンセルして戻る
              </button>
              <button
                onClick={() => setOpen(false)}
                className="mb-2 px-4 py-2 bg-blue-500 text-white rounded"
              >
                完了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
